import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import {
  Box,
  Typography,
  Button,
  TextField,
  Container,
  Paper,
  Grid,
  FormControl,
  InputAdornment,
  Checkbox,
  Snackbar,
  Alert,
  CircularProgress,
  Tab,
  Tabs,
  IconButton,
  FormControlLabel,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import BadgeIcon from '@mui/icons-material/Badge';
import { LocationOn, Person, Add, Edit, Delete, KeyboardArrowDown } from '@mui/icons-material';
import { useAppDispatch } from '../../redux/hooks';
import {
  getCustomerById,
  patchCustomer,
  createCustomer,
  createAddress,
  createContact,
  updateAddress,
  updateContact,
  patchAddress,
  patchContact,
  deleteAddress,
  deleteContact,
} from '../../services/customer-service';
import { validateCnpj, fetchAddressByCep } from '../../services/external-service';
import styles from './CustomerFormPage.module.css';
import BackButton from '../../components/BackButton/BackButton';
import { Customer, Address, Contact } from '../../types';

// Form data interfaces
interface AddressFormData {
  id?: string;
  street: string;
  number: string;
  city: string;
  state: string;
  zipCode: string;
  complement: string;
  isBilling: boolean;
  isDelivery: boolean;
  uf?: string;
}

interface ContactFormData {
  id?: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  isPrimary: boolean;
}

// Extended Customer interface with additional fields
type ExtendedCustomer = Customer;

const CustomerFormPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { setBreadcrumbs } = useBreadcrumb();
  const isEditMode = !!id;

  // Check if there's an activeTab in the location state and use it
  const initialTabValue = location.state?.activeTab !== undefined ? location.state.activeTab : 0;

  // Tabs state
  const [tabValue, setTabValue] = useState(initialTabValue);

  // Form state
  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [stateRegistration, setStateRegistration] = useState('');
  const [sector, setSector] = useState('');
  const [zipCode, setZipCode] = useState('');

  // Contact section state
  const [contacts, setContacts] = useState<ContactFormData[]>([]);

  // Contact validation errors
  const [contactNameError, setContactNameError] = useState('');
  const [contactEmailError, setContactEmailError] = useState('');
  const [contactPhoneError, setContactPhoneError] = useState('');
  const [contactRoleError, setContactRoleError] = useState('');

  // State for alerts
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error'>('success');

  // Loading state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingCustomer, setIsLoadingCustomer] = useState(false);

  const [isLoadingCnpj, setIsLoadingCnpj] = useState(false);
  const [cnpjError, setCnpjError] = useState('');
  const [cnpjValidated, setCnpjValidated] = useState(false);

  // Replace single address state with addresses list
  const [addresses, setAddresses] = useState<AddressFormData[]>([]);

  // Add this state for loading the CEP data
  const [isLoadingCep, setIsLoadingCep] = useState(false);
  const [cepError, setCepError] = useState('');

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Função para buscar os dados do cliente
  const fetchCustomerData = async (customerId: string) => {
    setIsLoadingCustomer(true);
    try {
      const response = await getCustomerById(customerId);

      if (response.success && response.data) {
        const customerData = response.data as ExtendedCustomer;

        // Preenche os campos do formulário
        setCompanyName(customerData.company_name);
        setPhone(formatPhone(customerData.phone || ''));
        setEmail(customerData.email);
        setCnpj(formatCnpj(customerData.cnpj || ''));
        setSector(customerData.sector);
        setStateRegistration(customerData.state_registration || '');

        // Load addresses if available
        if (customerData.addresses && customerData.addresses.length > 0) {
          const formattedAddresses = customerData.addresses.map(addr => ({
            id: addr.id,
            zipCode: formatZipCode(addr.zip_code || ''),
            street: addr.street || '',
            number: addr.number || '',
            complement: addr.complement || '',
            city: addr.city || '',
            state: addr.state || '',
            isBilling: addr.is_billing || false,
            isDelivery: addr.is_delivery || false,
          }));
          setAddresses(formattedAddresses);
        }

        // Load contacts from API response
        if (customerData.contacts && customerData.contacts.length > 0) {
          const formattedContacts = customerData.contacts.map(contact => ({
            id: contact.id,
            name: contact.name || '',
            email: contact.email || '',
            phone: formatPhone(contact.phone || ''),
            role: contact.role || '',
            isPrimary: contact.is_primary || false,
          }));
          setContacts(formattedContacts);
        } else {
          // Initialize with a default primary contact if none exists
          setContacts([
            {
              name: '',
              email: '',
              phone: '',
              role: '',
              isPrimary: true,
            },
          ]);
        }

        // Marca o CNPJ como validado para desabilitar a edição
        setCnpjValidated(true);
      }
    } catch (error) {
      console.error('Error fetching customer data:', error);
      showAlert('Erro ao carregar dados do cliente', 'error');
    } finally {
      setIsLoadingCustomer(false);
    }
  };

  useEffect(() => {
    // Setup initial state and handle data loading
    if (isEditMode && id) {
      // Fetch customer data
      fetchCustomerData(id);

      // Set breadcrumbs for edit mode
      setBreadcrumbs([
        { label: 'Home', path: '/', icon: <HomeIcon sx={{ fontSize: 16 }} /> },
        { label: 'Painel de Clientes', path: '/customers' },
        { label: companyName || 'Carregando...', path: `/customers/${id}` },
        { label: 'Editar' },
      ]);
    } else {
      // Set breadcrumbs for create mode
      setBreadcrumbs([
        { label: 'Home', path: '/', icon: <HomeIcon sx={{ fontSize: 16 }} /> },
        { label: 'Painel de Clientes', path: '/customers' },
        { label: 'Cadastro' },
      ]);
    }

    // Cleanup function for breadcrumbs
    return () => {
      setBreadcrumbs([{ label: 'Home', path: '/', icon: <HomeIcon sx={{ fontSize: 16 }} /> }]);
    };
  }, [isEditMode, id, companyName, setBreadcrumbs]);

  const validateCNPJ = (cnpj: string): { isValid: boolean; isComplete: boolean } => {
    const cleanCnpj = cnpj.replace(/[^\d]+/g, '');
    const isComplete = cleanCnpj.length === 14;

    if (!isComplete) {
      return { isValid: false, isComplete: false };
    }

    if (/^(\d)\1+$/.test(cleanCnpj)) {
      return { isValid: false, isComplete: true };
    }

    let tamanho = cleanCnpj.length - 2;
    let numeros = cleanCnpj.substring(0, tamanho);
    const digitos = cleanCnpj.substring(tamanho);
    let soma = 0;
    let pos = tamanho - 7;

    for (let i = tamanho; i >= 1; i--) {
      soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
      if (pos < 2) pos = 9;
    }

    let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado !== parseInt(digitos.charAt(0))) {
      return { isValid: false, isComplete: true };
    }

    tamanho = tamanho + 1;
    numeros = cleanCnpj.substring(0, tamanho);
    soma = 0;
    pos = tamanho - 7;

    for (let i = tamanho; i >= 1; i--) {
      soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
      if (pos < 2) pos = 9;
    }

    resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado !== parseInt(digitos.charAt(1))) {
      return { isValid: false, isComplete: true };
    }

    return { isValid: true, isComplete: true };
  };

  const showAlert = (message: string, severity: 'success' | 'error') => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  // Funções para mascarar entrada de dados
  const formatCnpj = (value: string): string => {
    // Remove caracteres não numéricos
    const numericValue = value.replace(/\D/g, '');
    // Limita a 14 dígitos
    const limitedValue = numericValue.slice(0, 14);

    // Aplica a máscara: XX.XXX.XXX/XXXX-XX
    return limitedValue
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  };

  const formatPhone = (value: string): string => {
    // Remove caracteres não numéricos
    const numericValue = value.replace(/\D/g, '');
    // Limita a 11 dígitos (formato celular com DDD)
    const limitedValue = numericValue.slice(0, 11);

    // Aplica a máscara: (XX) XXXXX-XXXX ou (XX) XXXX-XXXX
    if (limitedValue.length > 10) {
      return limitedValue.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2');
    } else {
      return limitedValue.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2');
    }
  };

  const formatZipCode = (value: string): string => {
    // Remove caracteres não numéricos
    const numericValue = value.replace(/\D/g, '');
    // Limita a 8 dígitos
    const limitedValue = numericValue.slice(0, 8);

    // Aplica a máscara: XXXXX-XXX
    return limitedValue.replace(/^(\d{5})(\d)/, '$1-$2');
  };

  // Add a function to check if all required fields in an address are filled
  const isAddressValid = (address: AddressFormData): boolean => {
    return !!(address.zipCode && address.street && address.number && address.city && address.state);
  };

  // Update the addNewAddress function to ensure new addresses are always delivery
  const addNewAddress = () => {
    setAddresses([
      ...addresses,
      {
        zipCode: '',
        street: '',
        number: '',
        complement: '',
        city: '',
        state: '',
        isBilling: false,
        isDelivery: true,
      },
    ]);
  };

  // Add a function to handle changes to address fields
  const handleAddressChange = (
    index: number,
    field: keyof AddressFormData,
    value: string | boolean
  ) => {
    const newAddresses = [...addresses];
    newAddresses[index] = {
      ...newAddresses[index],
      [field]: value,
    };
    setAddresses(newAddresses);
  };

  // Update the handleAddressDelete function to prevent deleting the billing address
  const handleAddressDelete = async (index: number) => {
    // Don't allow deleting the billing address (first address)
    if (index === 0) {
      showAlert('Não é possível excluir o endereço de faturamento', 'error');
      return;
    }

    const addressToDelete = addresses[index];

    // Se estiver em modo de edição e o endereço tem ID (já salvo no banco)
    if (isEditMode && addressToDelete.id && id) {
      try {
        // Mostra um indicador de loading ou mensagem
        setIsSubmitting(true);

        // Chama a API para excluir o endereço
        const response = await deleteAddress(id, addressToDelete.id);

        if (response.success) {
          showAlert('Endereço excluído com sucesso', 'success');
          // Remove da lista local apenas após sucesso na API
          const newAddresses = [...addresses];
          newAddresses.splice(index, 1);
          setAddresses(newAddresses);
        } else {
          showAlert('Erro ao excluir endereço', 'error');
        }
      } catch (error) {
        console.error('Error deleting address:', error);
        showAlert('Erro ao excluir endereço', 'error');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Caso de criação de cliente ou endereço novo - apenas remove da lista
      const newAddresses = [...addresses];
      newAddresses.splice(index, 1);
      setAddresses(newAddresses);
    }
  };

  const handleCnpjChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const cnpjValue = e.target.value;
    // Aplica formatação ao CNPJ
    const formattedCnpj = formatCnpj(cnpjValue);
    setCnpj(formattedCnpj);
    setCnpjError('');
    setCnpjValidated(false);

    // Verifica se o CNPJ está completo (14 dígitos)
    const numericCnpj = formattedCnpj.replace(/\D/g, '');
    if (numericCnpj.length !== 14) {
      return;
    }

    const { isValid, isComplete } = validateCNPJ(formattedCnpj);

    if (isComplete && !isValid) {
      setCnpjError('CNPJ informado está incorreto');
      showAlert('O CNPJ informado está incorreto. Por favor, verifique os dígitos.', 'error');
      return;
    }

    if (isValid) {
      setIsLoadingCnpj(true);

      try {
        const result = await validateCnpj(formattedCnpj);

        if (result.success && result.data) {
          const data = result.data;

          setCompanyName(data.razao_social);

          const ddd = data.estabelecimento.ddd1 || data.estabelecimento.ddd2 || '';
          const phone = data.estabelecimento.telefone1 || data.estabelecimento.telefone2 || '';
          const formattedPhone = formatPhone(ddd + phone);

          setPhone(formattedPhone);
          setEmail(data.estabelecimento.email);

          // Initialize the first address as billing address with option for delivery too
          setAddresses([
            {
              zipCode: formatZipCode(data.estabelecimento.cep),
              street: data.estabelecimento.logradouro,
              number: data.estabelecimento.numero,
              complement: data.estabelecimento.complemento,
              city: data.estabelecimento.cidade.nome,
              state: data.estabelecimento.estado.sigla,
              uf: data.estabelecimento.estado.sigla, // Set UF field directly from the state abbreviation
              isBilling: true, // Always billing
              isDelivery: false, // Optional delivery
            },
          ]);

          // Initialize with a primary contact (empty)
          setContacts([
            {
              name: '',
              email: '',
              phone: '',
              role: '',
              isPrimary: true,
            },
          ]);

          if (
            data.estabelecimento.inscricoes_estaduais &&
            data.estabelecimento.inscricoes_estaduais.length > 0
          ) {
            setStateRegistration(data.estabelecimento.inscricoes_estaduais[0].inscricao_estadual);
          }

          if (data.estabelecimento.atividade_principal) {
            setSector(data.estabelecimento.atividade_principal.descricao);
          }

          // Seleciona boleto e prazo de 30 dias automaticamente
          setCnpjValidated(true);
          showAlert('Dados do CNPJ carregados com sucesso!', 'success');
        }
      } catch (error) {
        console.error('Error validating CNPJ:', error);
        setCnpjError('Erro ao consultar CNPJ');
        showAlert('Erro ao consultar CNPJ', 'error');
      } finally {
        setIsLoadingCnpj(false);
      }
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const phoneValue = e.target.value;
    const formattedPhone = formatPhone(phoneValue);
    setPhone(formattedPhone);
  };

  const handleZipCodeChange = (index: number, zipValue: string) => {
    const formattedZip = formatZipCode(zipValue);
    handleAddressChange(index, 'zipCode', formattedZip);

    // Clear any previous error
    setCepError('');

    // Only proceed if ZIP code is complete (8 digits)
    const numericZip = formattedZip.replace(/\D/g, '');
    if (numericZip.length !== 8) {
      return;
    }

    // Fetch address data from ViaCEP API
    fetchAddressFromViaCep(index, formattedZip);
  };

  // Add this new function to fetch and process the address data
  const fetchAddressFromViaCep = async (index: number, cep: string) => {
    setIsLoadingCep(true);

    try {
      const result = await fetchAddressByCep(cep);

      if (result.success && result.data) {
        const data = result.data;

        // Update address fields
        const newAddresses = [...addresses];
        newAddresses[index] = {
          ...newAddresses[index],
          zipCode: formatZipCode(data.cep),
          street: data.logradouro,
          complement: data.complemento,
          city: data.localidade,
          state: data.uf,
          uf: data.uf, // Set UF field directly from API response
        };

        setAddresses(newAddresses);
        showAlert('Endereço carregado com sucesso!', 'success');
      } else if (result.error) {
        setCepError(result.error.message);
        showAlert(result.error.message, 'error');
      }
    } catch (error) {
      console.error('Error fetching address from CEP:', error);
      setCepError('Erro ao consultar CEP');
      showAlert('Erro ao consultar CEP', 'error');
    } finally {
      setIsLoadingCep(false);
    }
  };

  const shouldDisableFields = () => {
    // CNPJ field should always be disabled in edit mode
    if (isEditMode && cnpj) {
      return false;
    }

    // Em modo de criação, os campos só são habilitados após validação do CNPJ
    return !cnpjValidated || isLoadingCnpj;
  };

  // Function to validate email format
  const validateEmail = (email: string): boolean => {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  // Handler for email changes with validation
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);

    if (!value) {
      setEmailError('Email é obrigatório');
    } else if (!validateEmail(value)) {
      setEmailError('Formato de email inválido');
    } else {
      setEmailError('');
    }
  };

  // Handler for contact email changes with validation
  const handleContactEmailChange = (index: number, value: string) => {
    if (!value) {
      setContactEmailError('Email é obrigatório');
    } else if (!validateEmail(value)) {
      setContactEmailError('Formato de email inválido');
    } else {
      setContactEmailError('');
    }

    handleContactChange(index, 'email', value);
  };

  // Add a function to check if all required fields in a contact are filled
  const isContactValid = (contact: ContactFormData): boolean => {
    return !!(
      contact.name?.trim() &&
      contact.email?.trim() &&
      validateEmail(contact.email) &&
      contact.phone?.trim() &&
      contact.role?.trim()
    );
  };

  // Add a function to add a new contact to the list
  const addNewContact = () => {
    setContacts([
      ...contacts,
      {
        name: '',
        email: '',
        phone: '',
        role: '',
        isPrimary: false,
      },
    ]);
  };

  // Add a function to handle changes to contact fields
  const handleContactChange = (
    index: number,
    field: keyof ContactFormData,
    value: string | boolean
  ) => {
    const newContacts = [...contacts];
    newContacts[index] = {
      ...newContacts[index],
      [field]: value,
    };
    setContacts(newContacts);
  };

  // Add a function to handle contact deletion
  const handleContactDelete = async (index: number) => {
    // Don't allow deleting the primary contact
    if (index === 0) {
      showAlert('Não é possível excluir o contato principal', 'error');
      return;
    }

    const contactToDelete = contacts[index];

    // Se estiver em modo de edição e o contato tem ID (já salvo no banco)
    if (isEditMode && contactToDelete.id && id) {
      try {
        // Mostra um indicador de loading ou mensagem
        setIsSubmitting(true);

        // Chama a API para excluir o contato
        const response = await deleteContact(id, contactToDelete.id);

        if (response.success) {
          showAlert('Contato excluído com sucesso', 'success');
          // Remove da lista local apenas após sucesso na API
          const newContacts = [...contacts];
          newContacts.splice(index, 1);
          setContacts(newContacts);
        } else {
          showAlert('Erro ao excluir contato', 'error');
        }
      } catch (error) {
        console.error('Error deleting contact:', error);
        showAlert('Erro ao excluir contato', 'error');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Caso de criação de cliente ou contato novo - apenas remove da lista
      const newContacts = [...contacts];
      newContacts.splice(index, 1);
      setContacts(newContacts);
    }
  };

  // Update the handleContactPhoneChange function to work with the contacts array
  const handleContactPhoneChange = (index: number, value: string) => {
    const formattedPhone = formatPhone(value);
    handleContactChange(index, 'phone', formattedPhone);
  };

  // Update validateContactFields to work with contacts array
  const validateContactFields = (): boolean => {
    if (contacts.length === 0) {
      showAlert('Adicione pelo menos um contato', 'error');
      return false;
    }

    // Check if all contacts have valid information
    let isValid = true;
    for (let i = 0; i < contacts.length; i++) {
      if (!isContactValid(contacts[i])) {
        showAlert(`Verifique os dados do contato ${i + 1}`, 'error');
        isValid = false;
        break;
      }
    }

    return isValid;
  };

  // Validate form function should no longer check payment tab
  const validateForm = (): boolean => {
    let isValid = true;
    let firstInvalidTab = -1;

    // Tab 0: Customer Info validation
    if (!cnpjValidated && !isEditMode) {
      setCnpjError('CNPJ é obrigatório e deve ser validado');
      isValid = false;
      firstInvalidTab = 0;
    }

    if (!companyName?.trim()) {
      showAlert('Nome da empresa é obrigatório', 'error');
      isValid = false;
      firstInvalidTab = firstInvalidTab === -1 ? 0 : firstInvalidTab;
    }

    if (!phone?.trim()) {
      showAlert('Telefone é obrigatório', 'error');
      isValid = false;
      firstInvalidTab = firstInvalidTab === -1 ? 0 : firstInvalidTab;
    }

    if (!email?.trim()) {
      setEmailError('Email é obrigatório');
      isValid = false;
      firstInvalidTab = firstInvalidTab === -1 ? 0 : firstInvalidTab;
    } else if (!validateEmail(email)) {
      setEmailError('Formato de email inválido');
      isValid = false;
      firstInvalidTab = firstInvalidTab === -1 ? 0 : firstInvalidTab;
    }

    if (!sector?.trim()) {
      showAlert('Setor é obrigatório', 'error');
      isValid = false;
      firstInvalidTab = firstInvalidTab === -1 ? 0 : firstInvalidTab;
    }

    // Tab 1: Address validation
    if (addresses.length === 0) {
      showAlert('Adicione pelo menos um endereço', 'error');
      isValid = false;
      firstInvalidTab = firstInvalidTab === -1 ? 1 : firstInvalidTab;
    } else if (!addresses.some(addr => addr.isBilling)) {
      showAlert('É necessário definir um endereço de faturamento', 'error');
      isValid = false;
      firstInvalidTab = firstInvalidTab === -1 ? 1 : firstInvalidTab;
    } else {
      // Check each address fields
      for (let i = 0; i < addresses.length; i++) {
        const address = addresses[i];
        if (
          !address.zipCode ||
          !address.street ||
          !address.number ||
          !address.city ||
          !address.state
        ) {
          showAlert(`Preencha todos os campos obrigatórios do endereço ${i + 1}`, 'error');
          isValid = false;
          firstInvalidTab = firstInvalidTab === -1 ? 1 : firstInvalidTab;
          break;
        }
      }
    }

    // Tab 2: Contact validation
    if (contacts.length === 0) {
      showAlert('Adicione pelo menos um contato', 'error');
      isValid = false;
      firstInvalidTab = firstInvalidTab === -1 ? 2 : firstInvalidTab;
    } else {
      // Check each contact
      for (let i = 0; i < contacts.length; i++) {
        const contact = contacts[i];
        if (!contact.name?.trim()) {
          setContactNameError('Nome é obrigatório');
          isValid = false;
          firstInvalidTab = firstInvalidTab === -1 ? 2 : firstInvalidTab;
          break;
        }

        if (!contact.email?.trim()) {
          setContactEmailError('Email é obrigatório');
          isValid = false;
          firstInvalidTab = firstInvalidTab === -1 ? 2 : firstInvalidTab;
          break;
        } else if (!validateEmail(contact.email)) {
          setContactEmailError('Formato de email inválido');
          isValid = false;
          firstInvalidTab = firstInvalidTab === -1 ? 2 : firstInvalidTab;
          break;
        }

        if (!contact.phone?.trim()) {
          setContactPhoneError('Telefone é obrigatório');
          isValid = false;
          firstInvalidTab = firstInvalidTab === -1 ? 2 : firstInvalidTab;
          break;
        }

        if (!contact.role?.trim()) {
          setContactRoleError('Função é obrigatória');
          isValid = false;
          firstInvalidTab = firstInvalidTab === -1 ? 2 : firstInvalidTab;
          break;
        }
      }
    }

    if (firstInvalidTab >= 0) {
      setTabValue(firstInvalidTab);
    }

    return isValid;
  };

  // Add helper functions for formatting data
  const formatPhoneToSave = (phoneNumber: string): string => {
    return phoneNumber.replace(/\D/g, '');
  };

  const formatZipCodeToSave = (zipCode: string): string => {
    return zipCode.replace(/\D/g, '');
  };

  const formatCnpjToSave = (cnpjValue: string): string => {
    return cnpjValue.replace(/[^\d]+/g, '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Create the API payload without payment info
      const formattedPhone = formatPhoneToSave(phone);

      if (isEditMode && id) {
        // Edição de cliente existente
        const customerData: Partial<ExtendedCustomer> = {
          company_name: companyName,
          phone: formattedPhone,
          email,
          sector,
          state_registration: stateRegistration,
        };

        const response = await patchCustomer(id, customerData);

        if (response.success) {
          // Update addresses
          for (const address of addresses) {
            if (address.id) {
              // Update existing address
              const addressData = {
                street: address.street,
                number: address.number,
                city: address.city,
                state: address.state,
                zip_code: formatZipCodeToSave(address.zipCode),
                country: 'Brazil',
                is_default:
                  address.isBilling ||
                  (address.isDelivery && addresses.filter(a => a.isDelivery).length === 1),
                is_billing: address.isBilling,
                is_delivery: address.isDelivery,
                complement: address.complement,
                uf: address.state.substring(0, 2).toUpperCase(),
              };

              await patchAddress(id, address.id, addressData);
            } else {
              // Create new address
              const addressData = {
                street: address.street,
                number: address.number,
                city: address.city,
                state: address.state,
                zip_code: formatZipCodeToSave(address.zipCode),
                country: 'Brazil',
                is_default:
                  address.isBilling ||
                  (address.isDelivery && addresses.filter(a => a.isDelivery).length === 1),
                is_billing: address.isBilling,
                is_delivery: address.isDelivery,
                complement: address.complement,
                uf: address.state.substring(0, 2).toUpperCase(),
              };

              await createAddress(id, addressData);
            }
          }

          // Update contacts
          for (const contact of contacts) {
            if (contact.id) {
              // Update existing contact
              const contactData = {
                name: contact.name,
                email: contact.email,
                phone: formatPhoneToSave(contact.phone),
                role: contact.role,
                is_primary: contact.isPrimary,
              };

              await patchContact(id, contact.id, contactData);
            } else {
              // Create new contact
              const contactData = {
                name: contact.name,
                email: contact.email,
                phone: formatPhoneToSave(contact.phone),
                role: contact.role,
                is_primary: contact.isPrimary,
              };

              await createContact(id, contactData);
            }
          }

          showAlert('Cliente atualizado com sucesso!', 'success');
        }
      } else {
        // Criação de novo cliente
        const customerData = {
          company_name: companyName,
          phone: formattedPhone,
          email,
          cnpj: formatCnpjToSave(cnpj),
          state_registration: stateRegistration,
          sector,
        };

        const customerResponse = await createCustomer(customerData);

        if (customerResponse.success && customerResponse.data) {
          const newCustomerId = customerResponse.data.id;

          // Create addresses for the new customer
          for (const address of addresses) {
            const addressData = {
              street: address.street,
              number: address.number,
              city: address.city,
              state: address.state,
              zip_code: formatZipCodeToSave(address.zipCode),
              country: 'Brazil',
              is_default:
                address.isBilling ||
                (address.isDelivery && addresses.filter(a => a.isDelivery).length === 1),
              is_billing: address.isBilling,
              is_delivery: address.isDelivery,
              complement: address.complement,
              uf: address.state.substring(0, 2).toUpperCase(),
            };

            await createAddress(newCustomerId, addressData);
          }

          // Create contacts for the new customer
          for (const contact of contacts) {
            const contactData = {
              name: contact.name,
              email: contact.email,
              phone: formatPhoneToSave(contact.phone),
              role: contact.role,
              is_primary: contact.isPrimary,
            };

            await createContact(newCustomerId, contactData);
          }

          showAlert('Cliente cadastrado com sucesso!', 'success');
        }
      }

      setTimeout(() => {
        navigate('/customers');
      }, 2000);
    } catch (error) {
      console.error('Error saving customer:', error);
      showAlert('Erro ao salvar cliente. Por favor, tente novamente.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Navegação entre abas - update for only 3 tabs
  const handleNextTab = (e: React.MouseEvent) => {
    // Prevent default behavior to avoid form submission
    e.preventDefault();

    if (tabValue < 2) {
      setTabValue(tabValue + 1);
    }
  };

  const handlePreviousTab = (e: React.MouseEvent) => {
    // Prevent default behavior to avoid form submission
    e.preventDefault();

    if (tabValue > 0) {
      setTabValue(tabValue - 1);
    }
  };

  // Validação de informações por aba
  const validateInfoTab = (): boolean => {
    // Validação da aba de Informações do Cliente
    return cnpjValidated;
  };

  const validateAddressTab = (): boolean => {
    // Validação da aba de Endereços
    if (addresses.length === 0) {
      showAlert('Adicione pelo menos um endereço', 'error');
      return false;
    }

    // Check if there's a billing address
    if (!addresses.some(addr => addr.isBilling)) {
      showAlert('É necessário definir um endereço de faturamento', 'error');
      return false;
    }

    // Check if all addresses have the required fields
    for (let i = 0; i < addresses.length; i++) {
      if (!isAddressValid(addresses[i])) {
        showAlert(`Preencha todos os campos obrigatórios do endereço ${i + 1}`, 'error');
        return false;
      }
    }

    return true;
  };

  // Add back the missing renderCustomerInfoTab function
  const renderCustomerInfoTab = () => {
    return (
      <>
        <Box className={styles.sectionTitle}>
          <Person className={styles.sectionIcon} />
          <Typography variant="h6">Dados do Cliente</Typography>
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Box position="relative">
              <TextField
                fullWidth
                label="CNPJ"
                variant="outlined"
                size="small"
                value={cnpj}
                onChange={handleCnpjChange}
                required={!isEditMode}
                disabled={isSubmitting || isEditMode}
                error={!!cnpjError}
                helperText={cnpjError}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BadgeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  endAdornment: isLoadingCnpj ? (
                    <InputAdornment position="end">
                      <CircularProgress size={20} />
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nome da Empresa"
              variant="outlined"
              size="small"
              value={companyName}
              onChange={e => setCompanyName(e.target.value)}
              required
              disabled={isSubmitting || (shouldDisableFields() && !isEditMode)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Telefone"
              variant="outlined"
              size="small"
              value={phone}
              onChange={handlePhoneChange}
              required
              disabled={isSubmitting || (shouldDisableFields() && !isEditMode)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="E-mail"
              variant="outlined"
              size="small"
              type="email"
              value={email}
              onChange={handleEmailChange}
              required
              error={!!emailError}
              helperText={emailError}
              disabled={isSubmitting || (shouldDisableFields() && !isEditMode)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Inscrição Estadual"
              variant="outlined"
              size="small"
              value={stateRegistration}
              onChange={e => setStateRegistration(e.target.value)}
              disabled={isSubmitting || (shouldDisableFields() && !isEditMode)}
            />
          </Grid>
        </Grid>

        <Box className={styles.sectionTitle}>
          <BusinessIcon className={styles.sectionIcon} />
          <Typography variant="h6">Setor de atuação</Typography>
        </Box>
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Setor"
            variant="outlined"
            size="small"
            value={sector}
            onChange={e => setSector(e.target.value)}
            required
            disabled={isSubmitting || (shouldDisableFields() && !isEditMode)}
          />
        </Box>
      </>
    );
  };

  // Add back the missing renderAddressTab function
  const renderAddressTab = () => {
    return (
      <>
        <Box className={styles.sectionTitle}>
          <LocationOn className={styles.sectionIcon} />
          <Typography variant="h6">Endereços</Typography>
        </Box>

        {addresses.length > 0 ? (
          <>
            {addresses.map((address, index) => (
              <Paper
                key={index}
                variant="outlined"
                className={styles.addressSection}
                sx={{ mb: 3 }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body2" color="primary">
                    {index === 0 ? 'Endereço de Faturamento' : 'Endereço de Entrega'}
                  </Typography>

                  {index !== 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton
                        onClick={() => handleAddressDelete(index)}
                        size="small"
                        sx={{ color: '#000' }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>

                {index === 0 && (
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={address.isDelivery}
                            onChange={e =>
                              handleAddressChange(index, 'isDelivery', e.target.checked)
                            }
                            sx={{
                              color: '#83b81a',
                              '&.Mui-checked': {
                                color: '#83b81a',
                              },
                            }}
                          />
                        }
                        label="Deseja usar este endereço como endereço de entrega?"
                      />
                    </Grid>
                  </Grid>
                )}

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="CEP"
                      variant="outlined"
                      size="small"
                      value={address.zipCode}
                      onChange={e => {
                        handleZipCodeChange(index, e.target.value);
                      }}
                      required
                      disabled={isSubmitting || isLoadingCep}
                      error={!!cepError}
                      helperText={cepError}
                      InputProps={{
                        endAdornment: isLoadingCep ? (
                          <InputAdornment position="end">
                            <CircularProgress size={20} />
                          </InputAdornment>
                        ) : null,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Rua"
                      variant="outlined"
                      size="small"
                      value={address.street}
                      onChange={e => handleAddressChange(index, 'street', e.target.value)}
                      required
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Número"
                      variant="outlined"
                      size="small"
                      value={address.number}
                      onChange={e => handleAddressChange(index, 'number', e.target.value)}
                      required
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      label="Complemento"
                      variant="outlined"
                      size="small"
                      value={address.complement}
                      onChange={e => handleAddressChange(index, 'complement', e.target.value)}
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Cidade"
                      variant="outlined"
                      size="small"
                      value={address.city}
                      onChange={e => handleAddressChange(index, 'city', e.target.value)}
                      required
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Estado"
                      variant="outlined"
                      size="small"
                      value={address.state}
                      onChange={e => handleAddressChange(index, 'state', e.target.value)}
                      required
                      disabled={isSubmitting}
                    />
                  </Grid>
                </Grid>
              </Paper>
            ))}

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                type="button"
                variant="contained"
                startIcon={<Add />}
                onClick={addNewAddress}
                disabled={
                  isSubmitting ||
                  (addresses.length > 0 && !isAddressValid(addresses[addresses.length - 1]))
                }
                sx={{
                  bgcolor: '#000',
                  color: '#fff',
                  '&:hover': {
                    bgcolor: '#333',
                  },
                  '&.Mui-disabled': {
                    bgcolor: '#ccc',
                    color: '#666',
                  },
                }}
              >
                Adicionar Endereço
              </Button>
            </Box>
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {isEditMode
                ? 'Carregando endereços...'
                : 'Valide o CNPJ para carregar o endereço principal.'}
            </Typography>
          </Box>
        )}
      </>
    );
  };

  // Modify the renderContactTab function
  const renderContactTab = () => {
    return (
      <>
        <Box className={styles.sectionTitle}>
          <Person className={styles.sectionIcon} />
          <Typography variant="h6">Dados de Contato do Cliente Final</Typography>
        </Box>

        {contacts.length > 0 ? (
          <>
            {contacts.map((contact, index) => (
              <Paper
                key={index}
                variant="outlined"
                className={styles.addressSection}
                sx={{ mb: 3 }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
                  {index !== 0 && (
                    <IconButton
                      onClick={() => handleContactDelete(index)}
                      size="small"
                      sx={{ color: '#000' }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Nome"
                      variant="outlined"
                      size="small"
                      value={contact.name}
                      onChange={e => handleContactChange(index, 'name', e.target.value)}
                      required
                      error={index === 0 && !!contactNameError}
                      helperText={index === 0 && contactNameError}
                      disabled={isSubmitting}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Person fontSize="small" sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="E-mail"
                      variant="outlined"
                      size="small"
                      type="email"
                      value={contact.email}
                      onChange={e => handleContactEmailChange(index, e.target.value)}
                      required
                      error={
                        (index === 0 && !!contactEmailError) ||
                        (!validateEmail(contact.email) && contact.email !== '')
                      }
                      helperText={
                        (index === 0 && contactEmailError) ||
                        (!validateEmail(contact.email) && contact.email !== ''
                          ? 'Formato de email inválido'
                          : '')
                      }
                      disabled={isSubmitting}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Telefone"
                      variant="outlined"
                      size="small"
                      value={contact.phone}
                      onChange={e => handleContactPhoneChange(index, e.target.value)}
                      required
                      error={index === 0 && !!contactPhoneError}
                      helperText={index === 0 && contactPhoneError}
                      disabled={isSubmitting}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Função"
                      variant="outlined"
                      size="small"
                      value={contact.role}
                      onChange={e => handleContactChange(index, 'role', e.target.value)}
                      required
                      error={index === 0 && !!contactRoleError}
                      helperText={index === 0 && contactRoleError}
                      disabled={isSubmitting}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Person fontSize="small" sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {isEditMode ? 'Carregando contatos...' : 'Valide o CNPJ para adicionar contatos.'}
            </Typography>
          </Box>
        )}
      </>
    );
  };

  // Update the renderNavigationButtons function to account for only 3 tabs
  const renderNavigationButtons = () => {
    // Form buttons style (Next, Previous, Submit)
    const buttonStyle = {
      minWidth: '120px',
      backgroundColor: '#83b81a',
      color: 'white',
      '&:hover': {
        backgroundColor: '#6a9615',
      },
      '&.Mui-disabled': {
        backgroundColor: '#d9d9d9',
        color: '#a1a1a1',
      },
    };

    const isLastTab = tabValue === 2;

    return (
      <Box className={styles.buttonsContainer}>
        {tabValue > 0 && (
          <Button
            variant="outlined"
            onClick={handlePreviousTab}
            disabled={isSubmitting}
            sx={{
              color: isSubmitting ? '#a1a1a1' : '#83b81a',
              borderColor: isSubmitting ? '#d9d9d9' : '#83b81a',
              '&:hover': {
                borderColor: '#6a9615',
              },
              minWidth: '120px',
            }}
          >
            Voltar
          </Button>
        )}

        {!isLastTab ? (
          <Button
            variant="contained"
            onClick={handleNextTab}
            disabled={isSubmitting}
            sx={buttonStyle}
          >
            Avançar
          </Button>
        ) : (
          <Button
            variant="contained"
            type="submit"
            disabled={isSubmitting}
            sx={buttonStyle}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : undefined}
          >
            {isEditMode ? 'Atualizar' : 'Cadastrar'}
          </Button>
        )}
      </Box>
    );
  };

  // Renderização do componente principal
  return (
    <Box sx={{ flex: 1, bgcolor: '#f2f2f7' }}>
      <Box className={styles.container}>
        <BackButton sx={{ mb: 1 }} />

        <Typography variant="h5" sx={{ mb: 1 }}>
          {isEditMode ? 'Editar cliente' : 'Cadastro de cliente'}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          {isEditMode
            ? 'Atualize as informações do cliente conforme necessário.'
            : 'Cadastre um novo cliente para começar a vender e gerenciar suas compras de forma inteligente!'}
        </Typography>

        <Box className={styles.tabContainer}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="client tabs">
            <Tab
              label="Informações do Cliente"
              className={tabValue === 0 ? styles.tabActive : styles.tab}
            />
            <Tab label="Endereços" className={tabValue === 1 ? styles.tabActive : styles.tab} />
            <Tab
              label="Contato Comercial"
              className={tabValue === 2 ? styles.tabActive : styles.tab}
            />
          </Tabs>
        </Box>

        <form
          onSubmit={e => {
            // Only proceed with form submission if we're on the last tab and explicitly submitting
            if (tabValue !== 2) {
              e.preventDefault();
              return false;
            } else {
              handleSubmit(e);
            }
          }}
          noValidate
        >
          <Paper className={styles.paper}>
            {isLoadingCustomer ? (
              <Box className={styles.loadingContainer}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Carregando dados do cliente...</Typography>
              </Box>
            ) : (
              <>
                {tabValue === 0 && renderCustomerInfoTab()}
                {tabValue === 1 && renderAddressTab()}
                {tabValue === 2 && renderContactTab()}

                {renderNavigationButtons()}
              </>
            )}
          </Paper>
        </form>
      </Box>

      <Snackbar
        open={alertOpen}
        autoHideDuration={6000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setAlertOpen(false)} severity={alertSeverity} sx={{ width: '100%' }}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerFormPage;
